/**
 * Payment Service: Stripe Checkout integration for FounderDNA Premium
 */

import { loadStripe } from '@stripe/stripe-js';

export const PRODUCTS = {
  PREMIUM: {
    id: 'founder-dna-premium',
    name: 'FounderDNA Premium Report',
    price: 29,
    currency: 'USD',
    description:
      'Full founder operating profile with weekly plan, stress patterns, deep analysis, and team compatibility.',
  },
};

const PREMIUM_ACCESS_KEY = 'founderDnaPremiumAccess';
const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY || '';

const stripePromise = STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY) : null;

const isDevMockMode = () =>
  process.env.NODE_ENV === 'development' && !STRIPE_PUBLIC_KEY;

const getApiBase = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  return '';
};

export const initPaymentProcessor = async () => {
  if (!stripePromise) {
    if (isDevMockMode()) {
      console.warn('[payment] Dev mock mode: set REACT_APP_STRIPE_PUBLIC_KEY for real checkout');
      return true;
    }
    console.error('[payment] Missing REACT_APP_STRIPE_PUBLIC_KEY');
    return false;
  }

  try {
    await stripePromise;
    return true;
  } catch (error) {
    console.error('[payment] Failed to initialize Stripe:', error);
    return false;
  }
};

export const createCheckoutSession = async ({ productId = PRODUCTS.PREMIUM.id, customerEmail } = {}) => {
  const successUrl = `${window.location.origin}/payment/success`;

  if (isDevMockMode()) {
    await new Promise((resolve) => setTimeout(resolve, 800));
    window.location.href = `${successUrl}?session_id=mock_dev_session`;
    return { sessionId: 'mock_dev_session' };
  }

  const response = await fetch(`${getApiBase()}/api/create-checkout-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      productId,
      customerEmail: customerEmail || undefined,
      metadata: {
        source: 'founderdna_results',
      },
    }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || 'Could not start checkout');
  }

  if (data.url) {
    window.location.href = data.url;
    return { sessionId: data.id };
  }

  if (data.id && stripePromise) {
    const stripe = await stripePromise;
    const { error } = await stripe.redirectToCheckout({ sessionId: data.id });
    if (error) throw new Error(error.message);
    return { sessionId: data.id };
  }

  throw new Error('Invalid checkout response');
};

export const confirmPayment = async (sessionId) => {
  if (!sessionId) return false;

  if (isDevMockMode() && sessionId.startsWith('mock_')) {
    storePremiumAccess({
      accessToken: 'mock_dev_token',
      sessionId,
      productId: PRODUCTS.PREMIUM.id,
      purchaseDate: new Date().toISOString(),
    });
    return true;
  }

  const response = await fetch(
    `${getApiBase()}/api/verify-payment?session_id=${encodeURIComponent(sessionId)}`
  );

  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data.success || !data.accessToken) {
    return false;
  }

  storePremiumAccess({
    accessToken: data.accessToken,
    sessionId,
    productId: data.productId,
    purchaseDate: data.purchaseDate,
    customerEmail: data.customerEmail,
  });

  return true;
};

const storePremiumAccess = (record) => {
  localStorage.setItem(PREMIUM_ACCESS_KEY, JSON.stringify(record));
};

export const getPremiumAccess = () => {
  try {
    const raw = localStorage.getItem(PREMIUM_ACCESS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.accessToken) return null;
    return parsed;
  } catch (e) {
    return null;
  }
};

export const hasPremiumAccess = () => Boolean(getPremiumAccess()?.accessToken);

export const clearPremiumAccess = () => {
  localStorage.removeItem(PREMIUM_ACCESS_KEY);
};

export const enablePremiumForTesting = () => {
  if (process.env.NODE_ENV !== 'development') return;
  storePremiumAccess({
    accessToken: 'dev_test_token',
    sessionId: 'dev_test',
    productId: PRODUCTS.PREMIUM.id,
    purchaseDate: new Date().toISOString(),
  });
};

const paymentService = {
  PRODUCTS,
  createCheckoutSession,
  confirmPayment,
  hasPremiumAccess,
  getPremiumAccess,
  clearPremiumAccess,
  enablePremiumForTesting,
  initPaymentProcessor,
};

export default paymentService;
